/**
 * BasicInfoTab Component
 *
 * Tab content for editing basic information and providers.
 * Based on SETTINGS_FUNCTIONAL_SPEC.md Section 1.3 "Basic Info Tab"
 */

import { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import {
  User,
  Plus,
  X,
  Check,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import type {
  CaseFile,
  Provider,
  ProviderCategory,
  UpdateCaseFileResponse,
} from '../../types/settings';
import {
  PROVIDER_CATEGORY_LABELS,
  getProviderCategoryStyle,
} from '../../types/settings';

interface BasicInfoTabProps {
  caseFile: CaseFile;
  onUpdated: () => void;
}

interface AddProviderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (provider: Omit<Provider, 'id'>) => void;
}

function AddProviderModal({ isOpen, onClose, onAdd }: AddProviderModalProps): JSX.Element | null {
  const [name, setName] = useState('');
  const [specialty, setSpecialty] = useState('');
  const [category, setCategory] = useState<ProviderCategory>('medical');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && specialty.trim()) {
      onAdd({ name: name.trim(), specialty: specialty.trim(), category });
      setName('');
      setSpecialty('');
      setCategory('medical');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-ink/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-xl shadow-xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-ink">Add Provider</h3>
            <button onClick={onClose} className="btn-ghost p-2 -mr-2">
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="provider-name" className="label">Provider Name</label>
              <input
                id="provider-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Dr. Sarah Chen"
                className="input"
                required
              />
            </div>

            <div>
              <label htmlFor="provider-specialty" className="label">Specialty / Type</label>
              <input
                id="provider-specialty"
                type="text"
                value={specialty}
                onChange={(e) => setSpecialty(e.target.value)}
                placeholder="Pediatric Neurology"
                className="input"
                required
              />
            </div>

            <div>
              <label htmlFor="provider-category" className="label">Category</label>
              <select
                id="provider-category"
                value={category}
                onChange={(e) => setCategory(e.target.value as ProviderCategory)}
                className="input"
              >
                {(Object.entries(PROVIDER_CATEGORY_LABELS) as [ProviderCategory, string][]).map(
                  ([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  )
                )}
              </select>
            </div>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button type="submit" className="btn-primary flex-1">
                Add Provider
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export function BasicInfoTab({ caseFile, onUpdated }: BasicInfoTabProps): JSX.Element {
  // Form state
  const [name, setName] = useState(caseFile.name || '');
  const [preferredName, setPreferredName] = useState(caseFile.preferredName || '');
  const [dateOfBirth, setDateOfBirth] = useState(caseFile.dateOfBirth || '');
  const [primaryDiagnosis, setPrimaryDiagnosis] = useState(caseFile.primaryDiagnosis || '');
  const [notes, setNotes] = useState(caseFile.notes || '');
  const [providers, setProviders] = useState<Provider[]>(caseFile.providers || []);

  // UI state
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Track changes
  useEffect(() => {
    const basicInfoChanged =
      name !== caseFile.name ||
      preferredName !== (caseFile.preferredName || '') ||
      dateOfBirth !== caseFile.dateOfBirth ||
      primaryDiagnosis !== caseFile.primaryDiagnosis ||
      notes !== (caseFile.notes || '');

    const providersChanged =
      JSON.stringify(providers) !== JSON.stringify(caseFile.providers || []);

    setHasChanges(basicInfoChanged || providersChanged);
  }, [name, preferredName, dateOfBirth, primaryDiagnosis, notes, providers, caseFile]);

  // Reset form when caseFile changes
  useEffect(() => {
    setName(caseFile.name || '');
    setPreferredName(caseFile.preferredName || '');
    setDateOfBirth(caseFile.dateOfBirth || '');
    setPrimaryDiagnosis(caseFile.primaryDiagnosis || '');
    setNotes(caseFile.notes || '');
    setProviders(caseFile.providers || []);
  }, [caseFile]);

  const handleAddProvider = (provider: Omit<Provider, 'id'>) => {
    setProviders((prev) => [...prev, { ...provider, id: crypto.randomUUID() }]);
  };

  const handleRemoveProvider = (index: number) => {
    setProviders((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const updateCaseFile = httpsCallable<
        {
          caseFileId: string;
          updates: {
            name?: string;
            preferredName?: string;
            dateOfBirth?: string;
            primaryDiagnosis?: string;
            notes?: string;
            providers?: Provider[];
          };
        },
        UpdateCaseFileResponse
      >(functions, 'updateCaseFile');

      const result = await updateCaseFile({
        caseFileId: caseFile.caseFileId,
        updates: {
          name,
          preferredName: preferredName || undefined,
          dateOfBirth,
          primaryDiagnosis: primaryDiagnosis || undefined,
          notes: notes || undefined,
          providers,
        },
      });

      if (result.data.success) {
        setSuccess('Changes saved successfully');
        setHasChanges(false);
        onUpdated();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(result.data.error || 'Failed to save changes');
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* Success/Error Messages */}
      {success && (
        <div className="alert alert-success mb-6 flex items-center gap-2" role="status" aria-live="polite">
          <Check className="w-5 h-5" aria-hidden="true" />
          {success}
        </div>
      )}
      {error && (
        <div className="alert alert-error mb-6 flex items-center gap-2" role="alert">
          <AlertCircle className="w-5 h-5" aria-hidden="true" />
          {error}
        </div>
      )}

      {/* Basic Information Card */}
      <div className="card mb-6">
        <div className="p-6">
          <h3 className="text-lg font-bold text-ink mb-5">Basic Information</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="basic-full-name" className="label">Full Name</label>
              <input
                id="basic-full-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter full legal name"
                className="input"
              />
            </div>

            <div>
              <label htmlFor="basic-preferred-name" className="label">Preferred Name/Nickname</label>
              <input
                id="basic-preferred-name"
                type="text"
                value={preferredName}
                onChange={(e) => setPreferredName(e.target.value)}
                placeholder="What do they like to be called?"
                className="input"
              />
            </div>

            <div>
              <label htmlFor="basic-dob" className="label">Date of Birth</label>
              <input
                id="basic-dob"
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className="input"
              />
            </div>

            <div></div>
          </div>

          <div className="mt-6">
            <label htmlFor="basic-diagnoses" className="label">Primary Diagnoses/Conditions</label>
            <textarea
              id="basic-diagnoses"
              value={primaryDiagnosis}
              onChange={(e) => setPrimaryDiagnosis(e.target.value)}
              placeholder="List primary diagnoses, conditions, or disabilities..."
              className="input min-h-[100px] resize-y"
            />
          </div>

          <div className="mt-6">
            <label htmlFor="basic-notes" className="label">Additional Notes</label>
            <textarea
              id="basic-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any other important information..."
              className="input min-h-[100px] resize-y"
            />
          </div>
        </div>
      </div>

      {/* Current Providers Card */}
      <div className="card mb-6">
        <div className="p-6">
          <div className="flex items-start justify-between mb-5">
            <div>
              <h3 className="text-lg font-bold text-ink mb-1">Current Providers</h3>
              <p className="text-sm text-muted">
                Medical, therapy, school, and other care providers
              </p>
            </div>
            <button
              onClick={() => setShowAddProvider(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Provider
            </button>
          </div>

          {providers.length > 0 ? (
            <div className="space-y-2">
              {providers.map((provider, index) => {
                const categoryStyle = getProviderCategoryStyle(provider.category);
                return (
                  <div
                    key={provider.id || index}
                    className="flex items-center justify-between p-3 bg-airy rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-airy-gradient rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-evergreen" />
                      </div>
                      <div>
                        <div className="font-semibold text-ink">{provider.name}</div>
                        <div className="text-sm text-muted">{provider.specialty}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${categoryStyle.bg} ${categoryStyle.text}`}
                      >
                        {PROVIDER_CATEGORY_LABELS[provider.category]}
                      </span>
                      <button
                        onClick={() => handleRemoveProvider(index)}
                        className="w-6 h-6 rounded-full flex items-center justify-center text-muted hover:bg-soft-neutral hover:text-ink transition-colors"
                        aria-label="Remove provider"
                      >
                        <X className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 bg-airy rounded-lg text-center">
              <p className="text-muted">No providers added yet.</p>
              <button
                onClick={() => setShowAddProvider(true)}
                className="text-sm text-evergreen hover:underline mt-2"
              >
                Add your first provider
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="btn-primary flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>

      {/* Add Provider Modal */}
      <AddProviderModal
        isOpen={showAddProvider}
        onClose={() => setShowAddProvider(false)}
        onAdd={handleAddProvider}
      />
    </>
  );
}

export default BasicInfoTab;
